// Reading api2.govglance.org's /elections surface.
//
// Extracted from useLocalCandidates once a second caller appeared: the detail
// page has to normalize a row exactly the way the list did, or a candidate
// would read differently depending on which screen you opened them from.
//
// No auth. api2 is read-only and public -- do NOT send the api.govglance.org
// bearer token here.
//
// Fields are read through `pick` because the endpoint declares no schema.
// Names marked GUESS have not been confirmed against a live response; if
// something renders empty, those are the lines to check first.

export const API2_BASE = 'https://api2.govglance.org';

// api2 rejects an unrecognised level with a 422 rather than an empty list,
// which is the behaviour that makes this worth validating on the way out: an
// empty list would read as "this county has nobody running".
export const CANDIDATE_LEVELS = ['state', 'county', 'city', 'local'];

// Indiana alone files 8,670 non-federal candidates, so a state-wide query
// truncates at any sane limit. Callers are told when it did.
export const DEFAULT_LIMIT = 200;

const pick = (source, ...keys) => {
  if (!source || typeof source !== 'object') return null;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

// Some states file names inverted ("VINES, HENRY"), others do not. Flip when
// there is exactly one comma; anything carrying a suffix or an organisation
// name is left alone.
export const displayCandidateName = (name) => {
  if (!name) return '';

  const parts = String(name).split(',');
  if (parts.length !== 2) return String(name).trim();

  const [surname, given] = parts.map((part) => part.trim());
  if (!given) return surname;

  return `${given} ${surname}`;
};

export const formatCandidateDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const utc = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return `${utc.getMonth() + 1}-${utc.getDate()}-${utc.getFullYear()}`;
};

export const normalizeCandidate = (raw, index = 0) => {
  if (!raw || typeof raw !== 'object') return null;

  const name = pick(raw, 'candidate_name', 'name', 'full_name');
  if (!name) return null;

  // A candidate with nothing published carries no `contact` key at all, rather
  // than an empty one, so "filed no website" cannot be misread as "has none".
  // Most non-federal rows have none: it is fed by state filings that publish an
  // email and by FEC committee filings, and Iowa's county PDFs are neither.
  const contact = raw.contact && typeof raw.contact === 'object' ? raw.contact : null;

  const candidate = {
    // A real row id, confirmed against a live response. Everything that needs
    // to name one candidate uses it -- there is no need to compose an identity
    // out of name and office, which was built on the assumption that these rows
    // had none.
    id: pick(raw, 'id'),

    name: String(name).trim(),
    displayName: displayCandidateName(name),

    // Published verbatim. Iowa's county PDFs fold the party column into this
    // string ("Attorney General Democratic Republican") and leave `party`
    // empty; that is a parsing problem at the source, and guessing where the
    // office ends would mangle the offices that legitimately contain a party
    // name.
    office: pick(raw, 'office', 'office_name', 'contest', 'contest_name') || '',
    officeCode: pick(raw, 'office_code'),

    // Present but empty on many rows rather than absent.
    party: pick(raw, 'party', 'party_name') || '',
    district: pick(raw, 'district', 'district_name', 'district_number') || '',

    level: pick(raw, 'level') || '',
    stateAbbr: pick(raw, 'state_abbr', 'state') || '',
    county: pick(raw, 'county_name', 'county') || '',
    jurisdiction: pick(raw, 'place_name', 'jurisdiction_name', 'city_name') || '',

    // There is no election date on these rows -- only the cycle year and, where
    // a state records it, the date the candidate filed.
    year: pick(raw, 'election_year', 'year') || '',
    filedDate: formatCandidateDate(pick(raw, 'filed_date')),

    incumbency: pick(raw, 'incumbent_challenger'),
    status: pick(raw, 'candidate_status', 'status'),

    // Maryland and Virginia publish the candidate's own campaign address on the
    // filing. It sits on the candidate rather than inside contact, because the
    // federal committee data behind contact is keyed on an FEC candidate id
    // that only federal candidates have -- `cand_id` here, empty on every
    // non-federal row.
    campaignWebsite: pick(raw, 'campaign_website'),
    fecCandidateId: pick(raw, 'cand_id'),

    email: contact ? pick(contact, 'email') : null,
    phone: contact ? pick(contact, 'phone') : null,
    contactSources: contact && Array.isArray(contact.sources) ? contact.sources : [],

    sourceUrl: pick(raw, 'source_url', 'official_source_url'),

    // A path rather than a URL ("/elections/documents/42589/file"), so it is
    // absolute before anything renders it as a link. This is the copy the API
    // holds: useful precisely when the county has moved or deleted the original
    // PDF, which is the common case a year after an election.
    archivedSourceUrl: (() => {
      const path = pick(raw, 'archived_source_url');
      if (!path) return null;
      return /^https?:\/\//.test(path) ? path : `${API2_BASE}${path}`;
    })(),

    order: index,
  };

  candidate.key = candidate.id
    ? `id:${candidate.id}`
    : [candidate.level || 'local', candidate.office, candidate.name, index].join(':');

  candidate.hasDetail = Boolean(
    candidate.email ||
      candidate.phone ||
      candidate.campaignWebsite ||
      candidate.sourceUrl ||
      candidate.archivedSourceUrl
  );

  return candidate;
};

// One page of results per distinct query, for as long as the tab is open.
//
// The list and the detail page ask the same question in sequence: open a
// candidate, press back, and the list refetches from nothing -- spinner, then
// content, on every return. Iowa's state list is 28 rows that changed last on
// the ninth; refetching it because somebody looked at one of them and came back
// is a flash of empty screen bought with a round trip.
//
// A plain Map rather than anything with eviction. It is scoped to the tab, the
// values are one page of rows each, and a candidate list is not something a
// person opens hundreds of variants of in one sitting.
const cache = new Map();

/**
 * One page of candidates for a state, level and optional county.
 *
 * Past elections are excluded by the endpoint, so a candidate appearing twice
 * has genuinely filed for two offices. Nothing is deduplicated here --
 * collapsing those rows would hide a real filing.
 */
export const fetchLocalCandidates = async (
  { stateAbbr, level, county = '', year, limit = DEFAULT_LIMIT },
  { signal } = {}
) => {
  if (!stateAbbr) throw new Error('state is required');
  if (!CANDIDATE_LEVELS.includes(level)) throw new Error(`unknown level ${level}`);

  // URLSearchParams rather than string concatenation: a county name carries a
  // space ("Wayne County"), and the two-letter codes this was safe for are no
  // longer the only thing going into the query.
  const params = new URLSearchParams({
    state: String(stateAbbr).toUpperCase(),
    level,
    limit: String(limit),
  });
  // A county filter is only meaningful for a contest that belongs to a county.
  // Iowa files its statewide races into each county's own candidate PDF, so a
  // state-level row carries a county_name -- but level=state with county= is
  // rejected with a 422, because a statewide contest is not a county contest
  // however it was published.
  if (county && level !== 'state') params.set('county', county);
  if (year) params.set('year', String(year));

  const cacheKey = params.toString();
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const response = await fetch(`${API2_BASE}/elections/candidates?${params}`, { signal });
  if (!response.ok) {
    // api2 says why in the body -- an unknown level names the valid ones, and a
    // rejected parameter names itself. Throwing only the status discards the
    // one part of the answer that tells you what to change.
    let detail = '';
    try {
      const body = await response.text();
      detail = body ? ` - ${body.slice(0, 300)}` : '';
    } catch {
      // The status alone is still worth reporting.
    }

    const error = new Error(
      `api2 /elections/candidates responded ${response.status}${detail}`
    );
    error.status = response.status;
    error.query = params.toString();
    throw error;
  }

  const payload = await response.json();
  const rows = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
      ? payload
      : [];

  const result = {
    candidates: rows.map(normalizeCandidate).filter(Boolean),
    truncated: rows.length >= limit,
  };

  // Only a successful answer is kept. A failure is not a fact about the data,
  // and caching one would turn a moment of bad network into a page that stays
  // broken until the tab is closed.
  cache.set(cacheKey, result);

  return result;
};

// For a caller that needs to see past the cache -- a manual refresh, or a test.
export const clearLocalCandidateCache = () => cache.clear();

/**
 * The state's own election authority.
 *
 * Twenty-two states publish no bulk list of non-federal candidates at all. An
 * empty result there does not mean nobody is running, so rather than rendering
 * "no candidates on file" over a full ballot, the authority is offered instead.
 *
 * The state and federal levels are exempt from paging on this endpoint and come
 * back whatever the limit, which is why limit=1 is safe.
 */
export const fetchStateAuthority = async (stateAbbr, { signal } = {}) => {
  try {
    const params = new URLSearchParams({ state: String(stateAbbr).toUpperCase(), limit: '1' });
    const response = await fetch(
      `${API2_BASE}/elections/official-sources?${params}`,
      { signal }
    );
    if (!response.ok) return null;

    const payload = await response.json();
    const level = payload?.levels?.state;
    const agency = Array.isArray(level) ? level[0] : level;
    if (!agency) return null;

    const url = pick(agency, 'official_website', 'website', 'url');
    if (!url) return null;

    // Branch on worth_following, not on the verdict: `refused` means the site
    // declined our datacentre and opens fine in a browser, while `missing` and
    // `no_such_domain` mean there is nothing there for anyone. Sending someone
    // to a dead address is worse than sending them nowhere.
    const check = agency.link_checks?.official_website;
    if (check && check.worth_following === false) return null;

    return {
      agencyName: pick(agency, 'agency_name', 'name') || null,
      url,
    };
  } catch {
    // A courtesy on top of an already-empty list. Failing it quietly leaves the
    // plain empty state, which is still true.
    return null;
  }
};

export { pick };